import React, { useEffect, useState } from 'react';
import { animateScroll as scroll } from 'react-scroll';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Button,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import FielderLogoNegative from '@assets/icons/logo-login-icon-light.svg';
import { recoverPasswordRequest } from '@core/api/password/password.service';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import { useLanguage } from 'Context/LanguageContext';
import FielderLogoImage from '@assets/icons/logo-login-icon.svg';
import { useTheme } from '@mui/material/styles';

const PRIVACY_POLICY_LINK = 'https://www.appfielder.com/policy';
const RecoverPassword = () => {
  const [email, setEmail] = useState('');
  const lan = useLanguage();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logoSrc = isDarkMode ? FielderLogoNegative : FielderLogoImage;
  const fielderMsg = useFielderSnackBar();
  useEffect(() => {
    scroll.scrollToTop();
  }, []);
  const handleRecoverPassword = (e) => {
    e.preventDefault();
    const body = { email };
    const onSuccess = () => {
      fielderMsg({
        message: lan.recoverPasswordSuccessText,
        variant: 'success',
        closeButton: true,
        duration: 5000,
      });
      navigate('/');
    };
    const onError = ({ status }) => {
      if (status && status === 540) {
        fielderMsg({
          message: lan.recoverPasswordUserNotFound,
          variant: 'error',
          closeButton: true,
          duration: 5000,
        });
      } else {
        fielderMsg({
          message: lan.errorGralFail,
          variant: 'error',
          closeButton: true,
          duration: 5000,
        });
      }
    };
    const loadingConfig = { dispatch };
    recoverPasswordRequest(body, onSuccess, onError, loadingConfig);
  };
  const handleBack = () => {
    navigate('/login');
  };
  return (
    <Box
      sx={(theme) => ({
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
        px: 2,
      })}
    >
      <Grid container justifyContent="center">
        <Grid
          component={Paper}
          elevation={1}
          sx={(theme) => ({
            maxWidth: 550,
            width: '100%',
            px: 3,
            py: 4,
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
          })}
        >
          {/* Logo */}
          <Grid container justifyContent="center" sx={{ mb: 3 }}>
            <Box
              component="img"
              src={logoSrc}
              alt="Fielder logo"
              sx={{
                width: '40%',
                maxWidth: 260,
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
              }}
            />
          </Grid>
          {/* Title */}
          <Typography
            variant="h6"
            component="h1"
            sx={{ mb: 2 }}
          >
            {lan.recoverPassword}
          </Typography>
          {/* Form */}
          <Box
            component="form"
            noValidate
            onSubmit={handleRecoverPassword}
          >
            <TextField
              fullWidth
              type="email"
              name="email"
              id="correo"
              placeholder={lan.email}
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Grid
              container
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Grid size="auto">
                <Button
                  id="btn-provider-back"
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={handleBack}
                  sx={{ minWidth: 100, mt: 1 }}
                >
                  {lan.back}
                </Button>
              </Grid>
              <Grid size="auto">
                <Button
                  id="recover-password-submit"
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{ minWidth: 180, mt: 1 }}
                >
                  {lan.recoverPasswordBtn}
                </Button>
              </Grid>
            </Grid>
          </Box>
          {/* Footer / Version + Privacy */}
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="caption"
              sx={{ color: '#858f9b' }}
            >
              {`© ${process.env.FIELDER_APP_YEAR} v${process.env.FIELDER_APP_VERSION} `}
              <Link
                href={PRIVACY_POLICY_LINK}
                underline="hover"
                component="button"
                variant="caption"
              >
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
export default RecoverPassword;
export function withSnackbar(Component) {
  return function WrappedComponent(props) {
    const fielderMsg = useFielderSnackBar();
    return <Component {...props} fielderMsg={fielderMsg} />;
  };
}
//Original code:
/*import React, { Component } from 'react';
import LogoLogin from '@assets/icons/logo-login-main.svg';
import { connect } from 'react-redux';
import { animateScroll as scroll } from 'react-scroll';
import withRouter from '~/withRouter/withRouter.component'
import { recoverPasswordRequest} from '@core/api/password/password.service';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import './RecoverPassword.css';
class RecoverPassword extends Component {
	constructor(props) {
		super(props);
		this.state = {
			email: null,
		};
	}
	componentDidMount = () => {
		scroll.scrollToTop();
	};
	setFieldValue(value) {
		this.setState((prevState) => ({
			email: value,
		}));
	}
	handleRecoverPassword = (e) => {
		e.preventDefault();
		let body = {
			email: this.state.email,
		};


		const onSuccess = (response) => {
			let state = this.state;
			this.setState(state);
			this.props.fielderMsg({message: this.props.lan.recoverPasswordSuccessText, variant: 'success', closeButton: true, duration: 5000});
			this.props.navigate("/");
		}

		const onError = ({ status }) => {
			if (status && status === 540) {
				let state = this.state;
				this.setState(state);
				this.props.fielderMsg({message: this.props.lan.recoverPasswordUserNotFound, variant: 'error', closeButton: true, duration: 5000});
			} else {
				let state = this.state;
				this.setState(state);
				this.props.fielderMsg({message: this.props.lan.errorGralFail, variant: 'error', closeButton: true, duration: 5000});
			}
		};
		const loadingConfig = { dispatch: this.props.dispatch }
		recoverPasswordRequest(body, onSuccess, onError, loadingConfig)

	};
	render() {
		return (
			<React.Fragment>
				<div className='container mt-4'>
					<Row className='justify-content-center'>
						<div className='RecoverPassword mt-5'>
							<Row className='justify-content-center mt-3'>
								<img className='Login-img' src={LogoLogin} alt='' />
							</Row>
							<Form>
								<Row className='justify-content-center'>
									<Form.Label className='RecoverPassword-label text-center'>
										{this.props.lan.recoverPassword}
									</Form.Label>
								</Row>
								<FormGroup className='RecoverPassword-input'>
									<Form.Control
										type='email'
										name='email'
										id='correo'
										placeholder={this.props.lan.email}
										onChange={(e) => this.setFieldValue(e.target.value)}
									/>
								</FormGroup>
								<Row className='justify-content-center'>
									<Button
										className='Fielder-button RecoverPassword-button'
										onClick={this.handleRecoverPassword}
									>
										{this.props.lan.recoverPasswordBtn}
									</Button>
								</Row>
								<Row className='justify-content-center mt-4'>
									<p className='Login-color-text-privacy Fielder-mainpage-addtext-link-color'>
										&nbsp;
										{`© ${process.env.FIELDER_APP_YEAR} v${process.env.FIELDER_APP_VERSION}`}
										<a
											className='Register-ref Fielder-mainpage-addtext-link-color ml-1'
											href='https://www.appfielder.com/policy'
										>
											Privacy Policy
										</a>
										.
									</p>
								</Row>
							</Form>
						</div>
					</Row>
				</div>
			</React.Fragment>
		);
	}
}
export default connect()(withRouter(withSnackbar(RecoverPassword)));
export function withSnackbar(Component) {
    return function WrappedComponent(props) {
        const fielderMsg = useFielderSnackBar();
        return <Component {...props} fielderMsg={fielderMsg} />;
    }
}
*/
